import React, { useMemo } from 'react';
import {
  SignupFlowSyncCredentialCondition,
  SignupFlowSyncCredentialConditionGroup,
  SignupFlowQualificationSignalType,
  ResumeQualificationSignalField,
  IExtendedSignupFlow,
  StringOperator,
  getResumeScreeningConfigIdForConditionalFlow,
  ScreenFilter,
  SignupFlowConditionGroupType,
  SignupFlowLLMScreeningCondition,
  signupFlowHasConditions,
} from '@scale/shared-types';
import SignupFlowFastLaneQualificationCondition from './SignupFlowFastLaneQualificationCondition';
import {
  AlertDialog,
  Flex,
  Box,
  IconButton,
  Icon,
  Text,
  Button,
  Tabs,
  Switch,
} from '@scale/scaleui-radix';
import { faClose, faPlus, faMinus } from '@fortawesome/pro-regular-svg-icons';
import { useListJobConfigsQuery } from 'react-scale/corp/lib/api/growth';
import ResumeScreeningConfigAutocomplete from 'react-scale/components/GrowthCenter/OnboardingControlPanel/components/ResumeScreeningConfigAutocomplete';

interface SignupFlowConditionalQualificationModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  signupFlowConfig: Partial<IExtendedSignupFlow>;
  setSignupFlowConfig: (signupFlowConfig: Partial<IExtendedSignupFlow>) => void;
}

const createEmptySyncCondition = (): SignupFlowSyncCredentialCondition => ({
  signalType: SignupFlowQualificationSignalType.Resume,
  field: ResumeQualificationSignalField.School,
  operator: StringOperator.Equals,
  value: '',
});

const createEmptySyncConditionGroup = (): SignupFlowSyncCredentialConditionGroup => ({
  type: SignupFlowConditionGroupType.Sync,
  conditions: [createEmptySyncCondition()],
});

export const SignupFlowConditionalQualificationModal: React.FC<
  SignupFlowConditionalQualificationModalProps
> = ({ isModalOpen, setIsModalOpen, signupFlowConfig, setSignupFlowConfig }) => {
  const syncConditionGroups = useMemo((): SignupFlowSyncCredentialConditionGroup[] => {
    const conditions = signupFlowConfig.conditionalConfig?.conditions;

    if (!signupFlowHasConditions({ conditionalConfig: signupFlowConfig.conditionalConfig })) {
      return [];
    }

    return conditions?.filter(
      condition => condition.type === SignupFlowConditionGroupType.Sync,
    ) as SignupFlowSyncCredentialConditionGroup[];
  }, [signupFlowConfig?.conditionalConfig]);

  const updateSyncConditionGroups = (conditionGroups: SignupFlowSyncCredentialConditionGroup[]) => {
    // Factor out the LLM screening if there is one to make sure it's included in the final list
    const llmScreeningConfigId = getResumeScreeningConfigIdForConditionalFlow(
      signupFlowConfig.conditionalConfig,
    );
    const llmScreeningCondition: SignupFlowLLMScreeningCondition | null = llmScreeningConfigId
      ? {
          type: SignupFlowConditionGroupType.LLM,
          screeningConfigId: llmScreeningConfigId,
        }
      : null;

    setSignupFlowConfig({
      ...signupFlowConfig,
      conditionalConfig: {
        ...signupFlowConfig.conditionalConfig,
        operator: signupFlowConfig.conditionalConfig?.operator ?? 'and',
        conditions: [...conditionGroups, ...(llmScreeningCondition ? [llmScreeningCondition] : [])],
        academicCredentialVerificationRequired:
          signupFlowConfig.conditionalConfig?.academicCredentialVerificationRequired ?? false,
      },
    });
  };

  const updateAcademicCredentialVerificationRequired = (required: boolean) => {
    setSignupFlowConfig({
      ...signupFlowConfig,
      conditionalConfig: {
        ...signupFlowConfig.conditionalConfig,
        operator: signupFlowConfig.conditionalConfig?.operator ?? 'and',
        conditions: signupFlowConfig.conditionalConfig?.conditions ?? [],
        academicCredentialVerificationRequired: required,
      },
    });
  };

  const addSyncConditionGroup = () => {
    updateSyncConditionGroups([...syncConditionGroups, createEmptySyncConditionGroup()]);
  };

  const removeSyncConditionGroup = (groupIndex: number) => {
    updateSyncConditionGroups(
      syncConditionGroups.filter(
        (_: SignupFlowSyncCredentialConditionGroup, i) => i !== groupIndex,
      ),
    );
  };

  const addSyncCondition = (groupIndex: number) => {
    const newGroups = [...syncConditionGroups];
    newGroups[groupIndex].conditions.push(createEmptySyncCondition());
    updateSyncConditionGroups(newGroups);
  };

  const removeSyncCondition = (groupIndex: number, conditionIndex: number) => {
    const newGroups = [...syncConditionGroups];
    newGroups[groupIndex].conditions = newGroups[groupIndex].conditions.filter(
      (_: SignupFlowSyncCredentialCondition, i) => i !== conditionIndex,
    );

    // If group becomes empty, remove the group
    if (newGroups[groupIndex].conditions.length === 0) {
      newGroups.splice(groupIndex, 1);
    }

    updateSyncConditionGroups(newGroups);
  };

  const updateSyncCondition = (
    groupIndex: number,
    conditionIndex: number,
    updatedCondition: Partial<SignupFlowSyncCredentialCondition>,
  ) => {
    const newGroups = [...syncConditionGroups];
    newGroups[groupIndex].conditions[conditionIndex] = {
      ...newGroups[groupIndex].conditions[conditionIndex],
      ...updatedCondition,
    };
    updateSyncConditionGroups(newGroups);
  };

  const selectedResumeScreeningConfigId = getResumeScreeningConfigIdForConditionalFlow(
    signupFlowConfig.conditionalConfig,
  );
  const { data: allJobConfigs } = useListJobConfigsQuery(ScreenFilter.Growth);

  const selectedResumeScreeningConfigData = useMemo(() => {
    if (!selectedResumeScreeningConfigId) {
      return null;
    }
    return allJobConfigs?.find(jobConfig => jobConfig._id === selectedResumeScreeningConfigId);
  }, [allJobConfigs, selectedResumeScreeningConfigId]);

  const onResumeScreeningConfigChange = (value: { id: string; title: string }) => {
    const existingSyncConditions: Array<
      SignupFlowSyncCredentialConditionGroup | SignupFlowLLMScreeningCondition
    > =
      signupFlowConfig.conditionalConfig?.conditions?.filter(
        condition => condition.type === SignupFlowConditionGroupType.Sync,
      ) ?? [];

    const hasExistingResumeScreeningCondition =
      getResumeScreeningConfigIdForConditionalFlow(signupFlowConfig.conditionalConfig) !== null;
    if (!hasExistingResumeScreeningCondition && value.id) {
      existingSyncConditions.push({
        type: SignupFlowConditionGroupType.LLM,
        screeningConfigId: value.id,
      } as SignupFlowLLMScreeningCondition);
    }

    setSignupFlowConfig({
      ...signupFlowConfig,
      conditionalConfig: {
        operator: 'and',
        conditions: existingSyncConditions,
      },
    });
  };

  return (
    <Flex direction="column" gap="4">
      <AlertDialog.Root open={isModalOpen} onOpenChange={setIsModalOpen}>
        <AlertDialog.Content
          size="4"
          minWidth="80%"
          className="scale-tailwind scale-theme"
          style={{ overflow: 'visible' }}
        >
          <Flex justify="between" align="center">
            <AlertDialog.Title mb="2" align="center">
              <Text size="4" weight="medium">
                Conditional Qualifications
              </Text>
            </AlertDialog.Title>
            <AlertDialog.Cancel>
              <IconButton color="neutral" variant="ghost">
                <Icon icon={faClose} color="neutral" label="Close" size="3" />
              </IconButton>
            </AlertDialog.Cancel>
          </Flex>

          <Tabs.Root defaultValue="sync">
            <Tabs.List>
              <Tabs.Trigger value="sync">Sync Conditions</Tabs.Trigger>
              <Tabs.Trigger value="llm">LLM Screening</Tabs.Trigger>
            </Tabs.List>
            <Tabs.Content value="sync">
              <Flex mb="4" mt="4" direction="column" gap="1">
                <Text size="2" weight="medium">
                  Configure the qualification conditions to determine which applicants will qualify
                  for this Signup Flow. Click save after closing the modal to apply your changes.
                </Text>
                <Flex gap="2" mt="2">
                  <Text size="2">Verify academic credentials for this signup flow.</Text>
                  <Switch
                    size="2"
                    checked={
                      signupFlowConfig.conditionalConfig?.academicCredentialVerificationRequired ??
                      false
                    }
                    onCheckedChange={updateAcademicCredentialVerificationRequired}
                  />
                </Flex>
              </Flex>

              {syncConditionGroups.map((group, groupIndex) => (
                <Flex key={`group-${groupIndex}`} direction="column">
                  <Flex direction="row" gap="2" className="items-center">
                    <Flex direction="column" flexGrow="1">
                      <div className="border border-neutral-300 rounded-md p-4">
                        <Flex mb="2" className="items-center justify-between w-full" flexGrow="1">
                          <Text size="2" weight="medium">
                            Condition Group {groupIndex + 1}
                          </Text>
                        </Flex>

                        {group.conditions.map((condition, conditionIndex) => (
                          <SignupFlowFastLaneQualificationCondition
                            key={`condition-${conditionIndex}`}
                            condition={condition}
                            conditionIndex={conditionIndex}
                            groupIndex={groupIndex}
                            updateCondition={updateSyncCondition}
                            removeCondition={removeSyncCondition}
                            totalConditions={group.conditions.length}
                          />
                        ))}

                        {/* Add condition button */}
                        <Flex direction="row" gap="3" className="items-center">
                          <Box width="5%" className="justify-center"></Box>
                          <Box width="20%" className="justify-center">
                            <Button
                              color="accent"
                              variant="outline"
                              size="2"
                              onClick={() => addSyncCondition(groupIndex)}
                            >
                              <Icon label="Plus" icon={faPlus} />
                              Add condition
                            </Button>
                          </Box>
                        </Flex>
                      </div>
                    </Flex>
                    <Flex ml="2" align="center">
                      <IconButton
                        size="1"
                        color="neutral"
                        variant="ghost"
                        onClick={() => removeSyncConditionGroup(groupIndex)}
                        ml="0"
                      >
                        <Icon label="Remove condition" icon={faMinus} color="neutral" />
                      </IconButton>
                    </Flex>
                  </Flex>
                  {groupIndex < syncConditionGroups.length - 1 && (
                    <Flex justify="center" align="center">
                      <Flex py="2">
                        <Text size="2" weight="medium">
                          OR
                        </Text>
                      </Flex>
                    </Flex>
                  )}
                </Flex>
              ))}

              {/* Add group button */}
              <Flex align="center" mt="4">
                <Button color="accent" variant="outline" size="2" onClick={addSyncConditionGroup}>
                  <Icon label="Plus" icon={faPlus} />
                  Add group
                </Button>
              </Flex>
            </Tabs.Content>

            <Tabs.Content value="llm">
              <Flex mb="4" mt="4" direction="column" gap="1">
                <Text size="2">
                  Use an existing resume screening for users on this signup flow.
                </Text>
              </Flex>

              <ResumeScreeningConfigAutocomplete
                showViewButton
                showCreateButton
                buttonClassName="h-[32px]"
                selectedResumeScreeningConfig={{
                  id: selectedResumeScreeningConfigId ?? '',
                  title: selectedResumeScreeningConfigData?.name ?? '',
                }}
                setSelectedResumeScreeningConfig={onResumeScreeningConfigChange}
              />
            </Tabs.Content>
          </Tabs.Root>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </Flex>
  );
};
